'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sparkles,
  ArrowRight,
  ArrowLeft,
  Loader2,
  Star,
  Calendar,
  Clock,
  MapPin,
  User,
  Mail,
  Lock,
  Eye,
  EyeOff,
  Check,
} from 'lucide-react';
import { toast } from 'sonner';
import { signUp, confirmSignUp, autoSignIn, resendSignUpCode, signInWithRedirect } from 'aws-amplify/auth';
import PasswordStrength, { getPasswordStrength } from '@/components/auth/PasswordStrength';
import { saveSignupProfileDraft } from '@/lib/signup-profile-draft';
import ExperienceAtmosphere from '@/components/ui/ExperienceAtmosphere';
import ThemeToggle from '@/components/ui/ThemeToggle';

const StepIndicator = ({
  currentStep,
  totalSteps,
}: {
  currentStep: number;
  totalSteps: number;
}) => (
  <div className="flex items-center justify-center gap-3 mb-8">
    {Array.from({ length: totalSteps }, (_, i) => (
      <div key={i} className="flex items-center">
        <motion.div
          className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm border transition-all duration-500 ${
            i + 1 < currentStep
              ? 'bg-emerald-500 border-emerald-500 text-white'
              : i + 1 === currentStep
                ? 'manuscript-button text-[#1b160f] border-amber-500 shadow-lg shadow-amber-500/15'
                : 'bg-white/5 border-[#e8d7b0]/14 text-[#ead8b7]/72'
          }`}
          animate={i + 1 === currentStep ? { scale: [1, 1.06, 1] } : {}}
          transition={{ duration: 2, repeat: Infinity }}
        >
          {i + 1 < currentStep ? <Check size={16} /> : i + 1}
        </motion.div>
        {i < totalSteps - 1 && (
          <div
            className={`w-12 h-0.5 mx-2 transition-colors duration-500 ${i + 1 < currentStep ? 'bg-emerald-500' : 'bg-[#e8d7b0]/10'}`}
          />
        )}
      </div>
    ))}
  </div>
);

const getAuthErrorMessage = (error: unknown) => {
  if (error instanceof Error) return error.message;
  return '';
};

const INTENTION_OPTIONS = [
  {
    value: 'self_knowledge',
    label: 'Know myself',
    copy: 'Prioritize chart facts, palm baseline, and stable personality patterns.',
  },
  {
    value: 'love',
    label: 'Love',
    copy: 'Prioritize partner timing, compatibility, and emotional pattern checks.',
  },
  {
    value: 'career',
    label: 'Career',
    copy: 'Prioritize decision timing, momentum, and practical next steps.',
  },
  {
    value: 'daily_ritual',
    label: 'Daily ritual',
    copy: 'Prioritize one focused reading, one reflection, and one saved signal.',
  },
] as const;

export default function SignupPage() {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [step, setStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationCode, setConfirmationCode] = useState('');
  const [username, setUsername] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    birthDate: '',
    birthTime: '',
    birthPlace: '',
    primaryIntention: 'self_knowledge' as (typeof INTENTION_OPTIONS)[number]['value'],
  });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  useEffect(() => {
    try {
      const query = new URLSearchParams(window.location.search);
      const saved = JSON.parse(sessionStorage.getItem('youraura_pending_signup') || 'null');
      const pending = query.get('confirm') ? { username: query.get('username'), email: query.get('username') } : saved;
      if (typeof pending?.username === 'string' && pending.username) {
        setUsername(pending.username);
        setFormData(current => ({ ...current, email: pending.email || '' }));
        setShowConfirmation(true);
      }
    } catch { sessionStorage.removeItem('youraura_pending_signup'); }
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { getCurrentUser } = await import('aws-amplify/auth');
        await getCurrentUser();
        router.replace('/dashboard');
      } catch {
        setIsCheckingAuth(false);
      }
    };
    checkAuth();
  }, [router]);

  if (isCheckingAuth) {
    return (
      <div className="min-h-screen site-shell auth-shell flex items-center justify-center">
        <Loader2 className="animate-spin text-amber-400" size={48} />
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setIsLoading(true);

    try {
      const uniqueUsername = `user_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      saveSignupProfileDraft({
        name: formData.name,
        email: formData.email,
        birthDate: formData.birthDate,
        birthTime: formData.birthTime,
        birthTimeConfidence: formData.birthTime ? 'exact' : 'unknown',
        birthPlace: formData.birthPlace,
        primaryIntention: formData.primaryIntention,
      });

      await signUp({
        username: uniqueUsername,
        password: formData.password,
        options: {
          userAttributes: {
            email: formData.email,
            name: formData.name,
          },
          autoSignIn: true,
        },
      });

      setUsername(uniqueUsername);
      sessionStorage.setItem('youraura_pending_signup', JSON.stringify({ username: uniqueUsername, email: formData.email }));
      setShowConfirmation(true);
      toast.success('Check your email to open your dashboard');
    } catch (error: unknown) {
      console.error(error);
      const errorMessage = getAuthErrorMessage(error);
      const normalizedErrorMessage = errorMessage.toLowerCase();

      if (
        (error instanceof Error && error.name === 'UsernameExistsException') ||
        normalizedErrorMessage.includes('already exists') ||
        normalizedErrorMessage.includes('already registered') ||
        normalizedErrorMessage.includes('account already exists')
      ) {
        toast.error('An account with this email already exists. Please sign in instead.', {
          duration: 5000,
          action: {
            label: 'Sign In',
            onClick: () => router.push('/login'),
          },
        });
      } else if (normalizedErrorMessage.includes('password')) {
        toast.error(errorMessage);
      } else {
        toast.error(errorMessage || 'Failed to create account');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmation = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await confirmSignUp({
        username,
        confirmationCode,
      });

      sessionStorage.removeItem('youraura_pending_signup');
      try {
        const result = await autoSignIn();
        if (!result.isSignedIn) throw new Error('Sign in required');
        toast.success('Your brief space is ready');
        router.push('/dashboard');
      } catch {
        toast.success('Email verified. Sign in to continue.');
        router.push('/login');
      }
    } catch (error: unknown) {
      console.error(error);
      toast.error(getAuthErrorMessage(error) || 'Failed to verify account');
    } finally {
      setIsLoading(false);
    }
  };

  const stepTitles = [
    {
      title: 'Create Your Free Account',
      subtitle: 'Create your account first. Profile details come next.',
    },
    {
      title: 'Add Your Core Profile',
      subtitle: 'Anchor your daily brief to real timing, location, and intent.',
    },
  ];

  if (showConfirmation) {
    return (
      <div className="min-h-screen site-shell auth-shell text-white flex items-center justify-center p-4 pt-20 sm:pt-4 relative overflow-hidden">
        <ExperienceAtmosphere variant="auth" priority />
        <div className="absolute right-5 top-5 z-20"><ThemeToggle /></div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-md relative z-10"
        >
          <div className="relative">
            <div className="card-feature p-6 sm:p-8">
              <div className="text-center mb-8">
                <motion.div
                  className="w-20 h-20 manuscript-panel border border-emerald-400/20 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg shadow-green-500/10"
                  animate={{ scale: [1, 1.05, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <Mail className="text-emerald-300" size={32} />
                </motion.div>
                <h2 className="text-2xl font-bold font-playfair text-[#f7ead2] mb-2">
                  Verify your email
                </h2>
                <p className="text-[#ead8b7]/84 text-sm">
                  We sent a verification code to
                  <br />
                  <span className="text-foreground font-medium break-all">{formData.email}</span>
                  <br />
                  <span className="text-[#ead8b7]/68">
                    Confirm it to open your dashboard.
                  </span>
                </p>
              </div>

              <form onSubmit={handleConfirmation} className="space-y-6">
                <div className="relative">
                  <input
                    type="text"
                    value={confirmationCode}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    aria-label="Email verification code"
                    onChange={(e) => setConfirmationCode(e.target.value)}
                    placeholder="Enter 6-digit code"
                    className="w-full manuscript-input rounded-xl py-4 px-4 text-[#f7ead2] text-center text-2xl tracking-[0.5em] placeholder:text-[#d8c59f]/35 placeholder:text-base placeholder:tracking-normal focus:outline-none focus:border-green-400/50 focus:ring-2 focus:ring-green-400/20 transition-all"
                    maxLength={6}
                    required
                  />
                </div>

                <motion.button
                  type="submit"
                  disabled={isLoading || confirmationCode.length < 6}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full py-4 rounded-xl font-bold text-lg manuscript-button text-[#1b160f] shadow-lg shadow-green-500/15 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <Loader2 className="animate-spin" size={24} />
                  ) : (
                    <>
                      <Check size={20} />
                      Verify & Continue
                    </>
                  )}
                </motion.button>
              </form>

              <div className="mt-6 text-center">
                <button type="button" disabled={isLoading} className="block w-full mb-4 text-foreground underline disabled:opacity-50" onClick={async () => {
                  setIsLoading(true);
                  try { await resendSignUpCode({ username }); toast.success('New verification code sent.'); }
                  catch (error) { toast.error(getAuthErrorMessage(error) || 'Could not resend code.'); }
                  finally { setIsLoading(false); }
                }}>Resend verification code</button>
                <button
                  type="button"
                  onClick={() => { sessionStorage.removeItem('youraura_pending_signup'); setShowConfirmation(false); }}
                  className="text-muted-foreground hover:text-foreground text-sm transition-colors"
                >
                  ← Back to signup
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen site-shell auth-shell text-white flex items-center justify-center p-4 pt-20 sm:pt-4 relative overflow-hidden">
      <ExperienceAtmosphere variant="auth" priority />
      <div className="absolute right-5 top-5 z-20"><ThemeToggle /></div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="w-full max-w-lg relative z-10"
      >
        <div className="text-center mb-6">
          <Link href="/" className="inline-flex items-center gap-3 mb-6 group">
            <div className="card-icon card-icon-lg">
              <Sparkles className="text-accent" size={24} />
            </div>
            <span className="text-2xl font-bold tracking-widest font-playfair uppercase text-[#f7ead2] group-hover:text-amber-400 transition-colors">
              YourAura
            </span>
          </Link>
        </div>

        <StepIndicator currentStep={step} totalSteps={2} />

        <motion.div layout className="relative">
          <div className="card-feature p-6 sm:p-8">
            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                className="text-center mb-6"
              >
                <h2 className="text-2xl font-bold font-playfair text-[#f7ead2] mb-1">
                  {stepTitles[step - 1].title}
                </h2>
                <p className="text-sm text-[#ead8b7]/76">{stepTitles[step - 1].subtitle}</p>
              </motion.div>
            </AnimatePresence>

            <form onSubmit={handleSubmit}>
              <AnimatePresence mode="wait">
                {step === 1 && (
                  <motion.div
                    key="step1"
                    initial={{ opacity: 0, x: 30 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -30 }}
                    className="space-y-5"
                  >
                    <FieldLabel htmlFor="signup-name" icon={<User size={12} className="text-amber-400" />}>
                      Your Name
                    </FieldLabel>
                    <input
                      type="text"
                      required
                      id="signup-name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full manuscript-input rounded-xl px-4 py-4 focus:outline-none focus:border-accent/40 focus:ring-2 focus:ring-accent/20 transition-all text-[#f7ead2] placeholder:text-[#d8c59f]/35"
                      placeholder="What should we call you?"
                    />

                    <FieldLabel htmlFor="signup-email" icon={<Mail size={12} className="text-indigo-300" />}>
                      Email Address
                    </FieldLabel>
                    <input
                      type="email"
                      required
                      id="signup-email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full manuscript-input rounded-xl px-4 py-4 focus:outline-none focus:border-accent/40 focus:ring-2 focus:ring-accent/20 transition-all text-[#f7ead2] placeholder:text-[#d8c59f]/35"
                      placeholder="you@example.com"
                    />

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <FieldLabel htmlFor="signup-password" icon={<Lock size={12} className="text-purple-300" />}>
                          Password
                        </FieldLabel>
                        <div className="relative">
                          <input
                            type={showPassword ? 'text' : 'password'}
                            required
                            id="signup-password"
                            value={formData.password}
                            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                            className="w-full manuscript-input rounded-xl px-4 py-4 pr-10 focus:outline-none focus:border-accent/40 transition-all text-[#f7ead2] placeholder:text-[#d8c59f]/35"
                            placeholder="••••••••"
                          />
                          <button
                            type="button"
                            aria-label={showPassword ? "Hide password" : "Show password"}
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#d8c59f]/72 hover:text-[#f7ead2]"
                          >
                            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                          </button>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <FieldLabel htmlFor="signup-confirmPassword">Confirm password</FieldLabel>
                        <input
                          type="password"
                          required
                          id="signup-confirmPassword"
                          value={formData.confirmPassword}
                          onChange={(e) =>
                            setFormData({ ...formData, confirmPassword: e.target.value })
                          }
                          className="w-full manuscript-input rounded-xl px-4 py-4 focus:outline-none focus:border-accent/40 transition-all text-[#f7ead2] placeholder:text-[#d8c59f]/35"
                          placeholder="••••••••"
                        />
                      </div>
                    </div>

                    <PasswordStrength password={formData.password} />

                    <motion.button
                      type="button"
                      onClick={() => {
                        const { strength } = getPasswordStrength(formData.password);
                        if (strength === 'weak') {
                          toast.error('Please use a stronger password');
                          return;
                        }
                        if (formData.password !== formData.confirmPassword) {
                          toast.error('Passwords do not match');
                          return;
                        }
                        setStep(2);
                      }}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="w-full manuscript-button text-[#1b160f] font-bold py-4 rounded-xl shadow-lg shadow-amber-500/20 flex items-center justify-center gap-2 mt-6 group"
                    >
                      <span>Continue to Profile Details</span>
                      <ArrowRight
                        size={18}
                        className="group-hover:translate-x-1 transition-transform"
                      />
                    </motion.button>
                  </motion.div>
                )}

                {step === 2 && (
                  <motion.div
                    key="step2"
                    initial={{ opacity: 0, x: 30 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -30 }}
                    className="space-y-5"
                  >
                    <div className="manuscript-panel border border-amber-500/20 rounded-xl p-4 flex items-start gap-3">
                      <Star
                        className="text-amber-400 flex-shrink-0 mt-0.5"
                        size={18}
                        fill="currentColor"
                      />
                      <div>
                        <p className="text-sm text-amber-200 font-medium">Why we ask for this</p>
                        <p className="text-xs text-[#ead8b7]/76">
                          Your birth data anchors chart calculations and timing so the daily
                          brief can feel personal instead of generic.
                        </p>
                      </div>
                    </div>

                    <div>
                      <FieldLabel icon={<Sparkles size={12} className="text-amber-400" />}>
                        Main reason you are here
                      </FieldLabel>
                      <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                        {INTENTION_OPTIONS.map((option) => {
                          const isSelected = formData.primaryIntention === option.value;

                          return (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() =>
                                setFormData({ ...formData, primaryIntention: option.value })
                              }
                              className={`rounded-xl border p-3 text-left transition-all ${
                                isSelected
                                  ? 'border-amber-400/60 bg-amber-500/15 text-[#f7ead2]'
                                  : 'border-[#e8d7b0]/14 bg-white/5 text-[#ead8b7]/76 hover:border-amber-400/30'
                              }`}
                            >
                              <span className="block text-sm font-semibold">{option.label}</span>
                              <span className="mt-1 block text-xs leading-5 text-[#ead8b7]/70">
                                {option.copy}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <FieldLabel htmlFor="signup-birthDate" icon={<Calendar size={12} className="text-indigo-300" />}>
                      Date of Birth
                    </FieldLabel>
                    <input
                      type="date"
                      required
                      id="signup-birthDate"
                      value={formData.birthDate}
                      onChange={(e) => setFormData({ ...formData, birthDate: e.target.value })}
                      className="w-full manuscript-input rounded-xl px-4 py-4 focus:outline-none focus:border-accent/40 transition-all text-[#f7ead2] [color-scheme:dark]"
                    />

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <FieldLabel htmlFor="signup-birthTime" icon={<Clock size={12} className="text-purple-300" />}>
                          Time of Birth
                        </FieldLabel>
                        <input
                          type="time"
                          id="signup-birthTime"
                          value={formData.birthTime}
                          onChange={(e) => setFormData({ ...formData, birthTime: e.target.value })}
                          className="w-full manuscript-input rounded-xl px-4 py-4 focus:outline-none focus:border-accent/40 transition-all text-[#f7ead2] [color-scheme:dark]"
                        />
                        <p className="text-[11px] leading-4 text-[#ead8b7]/58">
                          Unknown is okay. We avoid precise house claims until you add it.
                        </p>
                      </div>
                      <div className="space-y-2">
                        <FieldLabel htmlFor="signup-birthPlace" icon={<MapPin size={12} className="text-emerald-300" />}>
                          Birth Place
                        </FieldLabel>
                        <input
                          type="text"
                          required
                          id="signup-birthPlace"
                          value={formData.birthPlace}
                          onChange={(e) => setFormData({ ...formData, birthPlace: e.target.value })}
                          className="w-full manuscript-input rounded-xl px-4 py-4 focus:outline-none focus:border-accent/40 transition-all text-[#f7ead2] placeholder:text-[#d8c59f]/35"
                          placeholder="City, Country"
                        />
                      </div>
                    </div>

                    <p className="text-[11px] leading-5 text-[#ead8b7]/62">
                      Palm context is optional later. You do not need a palm scan to start using
                      Daily Aura, horoscope, charts, numerology, tarot, dreams, and Guidance.
                    </p>

                    <label className="flex items-start gap-3 cursor-pointer group mt-4">
                      <div className="relative mt-0.5">
                        <input
                          type="checkbox"
                          checked={acceptedTerms}
                          onChange={(e) => setAcceptedTerms(e.target.checked)}
                          className="peer sr-only"
                        />
                        <div className="w-5 h-5 rounded border border-[#e8d7b0]/20 bg-white/5 peer-checked:bg-amber-500 peer-checked:border-amber-500 transition-all flex items-center justify-center">
                          {acceptedTerms && <Check size={12} className="text-black" />}
                        </div>
                      </div>
                      <span className="text-xs text-[#ead8b7]/80 leading-relaxed">
                        I agree to the{' '}
                        <Link href="/terms" className="text-amber-400 hover:underline">
                          Terms of Service
                        </Link>{' '}
                        and{' '}
                        <Link href="/privacy" className="text-amber-400 hover:underline">
                          Privacy Policy
                        </Link>
                        . Readings are reflective guidance, not medical, legal, financial, or
                        fixed-fate advice.
                      </span>
                    </label>

                    <div className="flex gap-3 mt-6">
                      <button
                        type="button"
                        onClick={() => setStep(1)}
                        className="px-6 py-4 manuscript-button-secondary rounded-xl text-[#f7ead2] font-medium transition-colors flex items-center gap-2"
                      >
                        <ArrowLeft size={16} />
                        Back
                      </button>
                      <motion.button
                        type="submit"
                        disabled={isLoading || !acceptedTerms}
                        whileHover={{ scale: acceptedTerms ? 1.02 : 1 }}
                        whileTap={{ scale: acceptedTerms ? 0.98 : 1 }}
                        className="flex-1 manuscript-button text-[#1b160f] font-bold py-4 rounded-xl shadow-lg shadow-amber-500/20 flex items-center justify-center gap-2 transition-all duration-500 disabled:opacity-50 disabled:cursor-not-allowed group relative overflow-hidden"
                      >
                        {isLoading ? (
                          <Loader2 className="animate-spin" size={20} />
                        ) : (
                          <>
                            <Star size={18} className="fill-current" />
                            <span>Create My Free Brief Space</span>
                          </>
                        )}
                      </motion.button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </form>

            <div className="mt-8 pt-6 border-t border-[#e8d7b0]/10 text-center">
              <p className="text-sm text-[#ead8b7]/74">
                Already have an account?{' '}
                <Link
                  href="/login"
                  className="text-accent hover:text-[#f7ead2] font-bold transition-colors hover:underline underline-offset-4"
                >
                  Sign In
                </Link>
              </p>
            </div>

            <div className="flex items-center gap-4 mt-6">
              <div className="flex-1 h-px bg-[#e8d7b0]/10" />
              <span className="text-xs text-[#ead8b7]/58 uppercase tracking-[0.2em]">or</span>
              <div className="flex-1 h-px bg-[#e8d7b0]/10" />
            </div>

            <motion.button
              type="button"
              onClick={async () => {
                setIsGoogleLoading(true);
                try {
                  await signInWithRedirect({ provider: 'Google' });
                } catch (error: unknown) {
                  console.error(error);
                  toast.error(getAuthErrorMessage(error) || 'Google sign-in failed');
                  setIsGoogleLoading(false);
                }
              }}
              disabled={isGoogleLoading}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full mt-4 manuscript-button-secondary rounded-xl py-4 px-6 flex items-center justify-center gap-3 transition-all disabled:opacity-50"
            >
              {isGoogleLoading ? (
                <Loader2 className="animate-spin text-[#f7ead2]" size={20} />
              ) : (
                <>
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  <span className="text-[#f7ead2] font-medium">Continue with Google</span>
                </>
              )}
            </motion.button>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="mt-8 text-center"
        >
          <p className="text-[10px] text-[#ead8b7]/52 tracking-[0.22em] uppercase">
            Built for daily brief habits, remembered context, and private reflection
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
}

function FieldLabel({ children, icon, htmlFor }: { children: React.ReactNode; icon?: React.ReactNode; htmlFor?: string }) {
  return (
    <label htmlFor={htmlFor} className="text-xs uppercase tracking-[0.22em] text-[#ead8b7]/88 pl-1 flex items-center gap-2">
      {icon}
      {children}
    </label>
  );
}
